import { Component, inject, signal, OnDestroy, OnInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { ISirena } from 'modelosSeguridad/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { IFilter, IQueryParam } from 'modelos/src';
import { MapaFacilData } from '../../../../auxiliares/mapa-facil/interfaces';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-detalles-integracion',
  templateUrl: './detalles-integracion.component.html',
  styleUrls: [
    '../../../../componentes/detalle/detalle-pagina.scss',
    './detalles-integracion.component.scss',
  ],
  standalone: false,
  providers: [DatePipe],
})
/**
 * Detalle de la integración de una sirena. Antes era un diálogo; ahora es una
 * página como los demás detalles: /main/sirenas/sirenas/detalles/:id, con el
 * id externo (o el _id o el chipId) y, opcional, `?cliente=` para la
 * búsqueda de respaldo por cliente.
 */
export class DetallesIntegracionComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private data: { idSirena?: string; idCliente?: string } = {};
  private params$?: Subscription;
  private queryParams$?: Subscription;
  public selectedTab = 0;

  public loading = signal(true);
  public sirena?: ISirena;

  private lista2 = inject(Lista2Service);
  private datePipe = inject(DatePipe);

  // Configuración del mapa
  public mapOptions?: MapaFacilData;

  /** El cliente, sin HTML (se calcula al dibujar: el índice puede llegar después). */
  get cliente(): string | undefined {
    if (HelperService.getTipoCliente() === 'Final') return undefined;
    const s = this.sirena;
    const cadena = this.clienteJerarquia.cadena(s?.cliente ?? s?.idCliente);
    return cadena[cadena.length - 1]?.nombre || s?.cliente?.nombre || undefined;
  }

  /** Las pestañas en orden, por nombre. Tiene que coincidir con la plantilla. */
  private pestanas(): string[] {
    return ['info', 'asignaciones'];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Fecha y hora, o null (app-dt-dato pone "Sin información"). */
  public fecha(valor?: string | Date | null): string | null {
    return valor ? this.datePipe.transform(valor, 'dd/MM/yyyy HH:mm') : null;
  }

  public siNo(valor?: boolean | null): string | null {
    if (valor === undefined || valor === null) return null;
    return valor ? 'Sí' : 'No';
  }

  public numero(valor?: number | null): string | null {
    return valor === undefined || valor === null ? null : `${valor}`;
  }

  public encendido(valor?: boolean | null): string | null {
    if (valor === undefined || valor === null) return null;
    return valor ? 'Encendido' : 'Apagado';
  }

  private isValidCoordinateArray(value: unknown): value is [number, number] {
    return (
      Array.isArray(value) &&
      value.length >= 2 &&
      Number.isFinite(value[0]) &&
      Number.isFinite(value[1])
    );
  }

  private resolveCoordinates(sirena: ISirena): [number, number] | null {
    const geojsonCoordinates = sirena.geojson?.coordinates;
    if (this.isValidCoordinateArray(geojsonCoordinates)) {
      return [geojsonCoordinates[0], geojsonCoordinates[1]];
    }

    const gps = sirena.ubicacionGps;
    if (gps && Number.isFinite(gps.lng) && Number.isFinite(gps.lat)) {
      return [gps.lng, gps.lat];
    }

    const manual = sirena.ubicacionManual;
    if (manual && Number.isFinite(manual.lng) && Number.isFinite(manual.lat)) {
      return [manual.lng, manual.lat];
    }

    return null;
  }

  public async listar() {
    this.loading.set(true);
    try {
      const idSirena = this.data.idSirena;
      const idCliente = this.data.idCliente;

      if (!idSirena) {
        console.warn('[DetallesIntegracion] idSirena vacío, abortando');
        return;
      }

      // Intento 1: buscar por _id en el servidor (funciona para usuario dueño directo).
      const filterById: IFilter<ISirena> = { _id: idSirena };
      const queryById: IQueryParam = {
        filter: JSON.stringify(filterById),
        includeChildren: true,
        limit: 1,
      };
      const listado = await firstValueFrom(this.lista2.sirenas(queryById));
      let res = listado?.datos?.[0];

      // Intento 2: el backend puede no aplicar includeChildren con filtro _id (usuario padre).
      // Se listan todas las sirenas visibles y se busca en memoria.
      if (!res) {
        const queryAll: IQueryParam = { includeChildren: true };
        const listadoAll = await firstValueFrom(this.lista2.sirenas(queryAll));
        res = listadoAll?.datos?.find(
          (s) => s._id === idSirena || s.chipId === idSirena,
        );
      }

      // Intento 3: fallback por endpoint byId con cliente explícito.
      if (!res && idCliente) {
        res = await firstValueFrom(this.lista2.sirenaById(idSirena, idCliente));
      }

      if (!res) {
        return;
      }

      this.sirena = res;
      this.mapOptions = undefined;

      // Configurar opciones del mapa una sola vez
      const coordinates = this.resolveCoordinates(this.sirena);
      if (coordinates) {
        this.mapOptions = {
          markers: [
            {
              coor: coordinates,
              label: 'sirena',
              data: this.sirena,
              text: this.sirena.chipId,
              centrar: true,
            },
          ],
          center: coordinates,
          zoom: 16,
          clustering: {
            enabled: false,
          },
        };
      }
    } catch (error) {
      console.error('Error al listar sirena:', error);
      this.sirena = undefined;
    } finally {
      this.loading.set(false);
    }
  }

  ngOnInit(): void {
    // Al cambiar el id de la ruta Angular reutiliza el componente
    this.params$ = this.route.paramMap.subscribe((params) => {
      this.data = {
        idSirena: params.get('id') ?? undefined,
        idCliente:
          this.route.snapshot.queryParamMap.get('cliente') ?? undefined,
      };
      this.sirena = undefined;
      void this.listar();
    });
    this.queryParams$ = this.route.queryParamMap.subscribe((qp) => {
      const tab = qp.get('tab');
      const i = tab ? this.pestanas().indexOf(tab) : 0;
      this.selectedTab = i >= 0 ? i : 0;
    });
  }

  ngOnDestroy(): void {
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
